/* istanbul ignore file */

import { useCallback } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';

import { useCart } from '@order/hooks';

import { useConflictReview } from '../../../../screens/ReorderingScreen';
import type { LocationSearchMachineContext } from '../../machine';
import { useNavigateToMenuFromLocation } from '../useNavigateToMenuFromLocation';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides navigation actions and helpers for Location Search.
 */
export const useLocationSearchNavigation = () => {
  const navigation = useNavigation();
  const { params } = useRoute();

  const { cart, isCartEmpty } = useCart();
  const conflictReview = useConflictReview();
  const navigateToMenu = useNavigateToMenuFromLocation();

  // ─────────────────────────────────────────────────────────────────────

  const routeParams = (params ??
    {}) as MaybeRouteParamsWithLocationSearchTypeParam;
  const hasLocationSearchParam = Object.prototype.hasOwnProperty.call(
    routeParams,
    'locationSelectionType',
  );
  const locationSearchRouteParam = getLocationSearchRouteParam(routeParams);

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Initiates a navigation event for pickup and outpost locations
   * based on the active location context.
   */
  const navigateToLocation = useCallback(
    (context: LocationSearchMachineContext) => {
      const { locationSearchType: orderType, activeLocation } = context;
      const cartRestaurantSlug = cart?.restaurant?.slug;

      if (!activeLocation) return;

      const {
        id: restaurantId,
        name: restaurantName,
        restaurantSlug,
        isOutpost,
      } = activeLocation;

      // ─── Possible Reorder ────────────────────────────────────────────────

      const shouldStartReorder =
        !isCartEmpty && cartRestaurantSlug !== restaurantSlug;

      // if your bag belongs to a different restaurant, start reorder flow
      if (shouldStartReorder) {
        void conflictReview({
          orderId: cart?.id,
          orderType: isOutpost ? 'outpost' : 'pickup',
          restaurantId,
          restaurantName,
          restaurantSlug,
          finishOnMenu: true,
        });

        return;
      }

      // ─────────────────────────────────────────────────────────────────────

      navigateToMenu({
        orderType,
        restaurantId,
        restaurantName,
        restaurantSlug,
      });
    },
    [
      cart?.id,
      cart?.restaurant?.slug,
      conflictReview,
      isCartEmpty,
      navigateToMenu,
    ],
  );

  /**
   * Initiates a navigation event for delivery location
   * based on the active location context.
   */
  const navigateToDeliveryLocation = useCallback(
    (context: LocationSearchMachineContext) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee = 0,
        name: deliveryAddressName,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      navigateToMenu({
        orderType: 'delivery',
        restaurantId,
        restaurantName,
        restaurantSlug,
        deliveryAddressName,
        deliveryOrderDetail: {
          addressId,
          tip: 0,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
      });
    },
    [navigateToMenu],
  );

  /**
   * Sets the route param for active current location search type if the current route support that.
   */
  const setLocationSearchTypeRouteParam = useCallback(
    (context: LocationSearchMachineContext) => {
      if (!hasLocationSearchParam) return;

      const { locationSearchType: locationSelectionType } = context;

      navigation.setParams({ locationSelectionType } as Parameters<
        typeof navigation.setParams
      >[0]);
    },
    [hasLocationSearchParam, navigation],
  );

  return {
    locationSearchRouteParam,
    navigateToLocation,
    navigateToDeliveryLocation,
    setLocationSearchTypeRouteParam,
  };
};

// ─── Utils ───────────────────────────────────────────────────────────────────

/**
 * Attempts to return `locationSelectionType` from the current route params
 */
function getLocationSearchRouteParam(
  routeParams: MaybeRouteParamsWithLocationSearchTypeParam,
): LocationSearchType | undefined {
  const { locationSelectionType } = routeParams ?? {};

  if (locationSelectionType === 'pickup') return 'pickup';

  if (locationSelectionType === 'outpost') return 'outpost';

  if (locationSelectionType === 'delivery') return 'delivery';
}

// ─── Type ────────────────────────────────────────────────────────────────────

type MaybeRouteParamsWithLocationSearchTypeParam = {
  readonly locationSelectionType?: string;
};

type LocationSearchType = LocationSearchMachineContext['locationSearchType'];
